import {
  Button, Card, DateInput, ErrorState, Field, Notice, Permissions, Row, Stack, StatusBadge, invalidDateIn,
  shiftIsoDate, todayIso, useApi, useI18n, usePermission,
  type TenantProfileResponse,
} from '@daghfal/core';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTrialLabel } from '../trial';
import { Detail } from './Detail';

/**
 * Where a tenant stands (FR-PLT-04, C6): its subscription and trial, who to call, when anything last happened
 * there, and what its files take. A tenant on its trial is activated or given a new last day here, behind `platform.billing.manage`.
 */
export function SubscriptionCard({ tenant, onChanged }: {
  tenant: TenantProfileResponse;
  onChanged: (updated: TenantProfileResponse) => void;
}) {
  const { t, formatDate, formatDateTime, formatMegabytes, formatPhone } = useI18n();
  const trialLabel = useTrialLabel();
  const canManageBilling = usePermission(Permissions.platform.manageBilling);
  const { subscription, owner } = tenant;
  const onTrial = tenant.status === 'Trial';

  return (
    <Card title={t('tenant.subscription')}>
      <Stack>
        <dl className="dg-grid" style={{ margin: 0 }}>
          <Detail label={t('tenants.column.status')} value={<StatusBadge status={tenant.status} />} />
          <Detail label={t('tenant.plan')} value={subscription?.planNameAr ?? t('tenant.plan.none')} />
          {onTrial && subscription?.trialEndsAt && (
            <Detail
              label={t('tenant.trial.lastDay')}
              value={trialLabel(subscription.trialEndsAt, tenant.timeZone)}
            />
          )}
          <Detail
            label={t('tenant.lastActivity')}
            value={tenant.lastActivityAt ? formatDateTime(tenant.lastActivityAt) : t('tenant.lastActivity.none')}
          />
          <Detail
            label={t('tenant.storage')}
            value={tenant.storage
              ? t('tenant.storage.value', {
                size: formatMegabytes(tenant.storage.bytes),
                date: formatDate(tenant.storage.measuredOn),
              })
              : t('tenant.storage.none')}
          />
          <Detail
            label={t('tenant.owner')}
            value={owner
              ? (
                <Stack tight>
                  <span>{owner.fullName ?? '—'} <StatusBadge status={owner.status} /></span>
                  <span dir="ltr">{formatPhone(owner.phone)}</span>
                  {owner.email && <span dir="ltr">{owner.email}</span>}
                </Stack>
              )
              : t('tenant.owner.none')}
          />
        </dl>

        {onTrial && canManageBilling && <TrialActions tenant={tenant} onChanged={onChanged} />}
      </Stack>
    </Card>
  );
}

/** Activating ends the trial today; extending moves its last day, and the reminder is sent again before it. */
function TrialActions({ tenant, onChanged }: {
  tenant: TenantProfileResponse;
  onChanged: (updated: TenantProfileResponse) => void;
}) {
  const api = useApi();
  const { t, settings } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const [endsOn, setEndsOn] = useState('');
  const [missing, setMissing] = useState(false);

  // The API reads the day in the tenant's zone, so the earliest it takes is tomorrow there.
  const earliest = shiftIsoDate(todayIso({ ...settings, timeZone: tenant.timeZone }), 1);

  const activate = useMutation({
    mutationFn: () => api.platform.activateTenant(tenant.id),
    onSuccess: (updated) => { setConfirming(false); onChanged(updated); },
  });

  const extend = useMutation({
    mutationFn: () => api.platform.extendTrial(tenant.id, endsOn),
    onSuccess: (updated) => { setEndsOn(''); onChanged(updated); },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget)) return;
    setMissing(!endsOn);
    if (endsOn) extend.mutate();
  };

  return (
    <Stack>
      {activate.isError && <ErrorState error={activate.error} />}
      {confirming ? (
        <>
          <Notice tone="info">{t('tenant.activate.hint')}</Notice>
          <Row>
            <Button busy={activate.isPending} busyLabel={t('action.saving')} onClick={() => activate.mutate()}>
              {t('tenant.activate.confirm')}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>{t('action.cancel')}</Button>
          </Row>
        </>
      ) : (
        <Row>
          <Button onClick={() => setConfirming(true)}>{t('tenant.activate')}</Button>
        </Row>
      )}

      <form noValidate onSubmit={submit}>
        <Stack>
          <Field
            label={t('tenant.extend.endsOn')}
            hint={t('tenant.extend.hint')}
            error={missing ? t('tenant.extend.required') : null}
            required
          >
            {(props) => (
              <DateInput
                {...props}
                value={endsOn}
                min={earliest}
                // The field is marked invalid while the "choose a day" message shows, which `invalidDateIn` would
                // then stop every later submit on; a day chosen takes the message away.
                onChange={(next) => { setEndsOn(next); if (next) setMissing(false); }}
              />
            )}
          </Field>
          {extend.isError && <ErrorState error={extend.error} />}
          <Row>
            <Button type="submit" variant="secondary" busy={extend.isPending} busyLabel={t('action.saving')}>
              {t('tenant.extend')}
            </Button>
          </Row>
        </Stack>
      </form>
    </Stack>
  );
}
